import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { uiLang } from './locale';

/**
 * Traducción de los errores del API al idioma del dashboard (WS-2311).
 *
 * El backend acompaña cada error de un código estable además del `message` que
 * redacta él (en castellano fijo o en el idioma del navegador). Aquí se lee ese
 * código y, si hay traducción, se sustituye el texto; si no, se deja el
 * `message` tal cual. Catálogo: backend
 * `.documentation/secciones/errores/catalogo-codigos-api.md`.
 *
 * - `error_type` (snake_case): API_ERRORS.TYPE.<valor>.
 * - `code`: solo los 20 de autenticación (AuthErrorCodeEnum), buscados por
 *   valor en API_ERRORS.AUTH. La misma clave lleva códigos de negocio (Rides,
 *   chat, pooling) y de WhatsApp, también en mayúsculas, sin catálogo cerrado:
 *   la forma no los distingue, así que cualquier valor que no esté en el
 *   diccionario se deja con su `message`.
 * - 422 de `api/backoffice/*` con `failed_rules`: cada campo se recompone con
 *   API_ERRORS.RULE.<regla>, solo si se conocen todas sus reglas, y solo en
 *   inglés y catalán: muchos FormRequest del backend traen mensajes propios en
 *   castellano más concretos que el texto genérico de la regla
 *   («La bonificación del invitado no puede pasar de 1.000 €»).
 *
 * Nunca se borra ni se renombra una clave: el texto original queda en
 * `server_message` (y `server_errors` para la validación).
 */

/**
 * `error_type` cuyo `message` trae el dato concreto (motivo, mínimo, precio
 * recomendado, respuesta de Stripe). Sustituirlo por un texto genérico perdería
 * información, así que se deja el `message` del servidor.
 */
const KEEP_SERVER_MESSAGE = new Set([
  'bussines_logic_error',
  'invalid_minimum_amount',
  'plan_not_eligible',
  'price_error',
  'stripe_failed',
]);

/** Mensaje de Laravel cuando hay más de un error: «(and 2 more errors)». */
const MORE_ERRORS = /\s*\(.*\d+.*\)\s*$/;

/** Texto traducido para `key`, o null si la clave no existe en el idioma activo. */
function lookup(translate: TranslateService, key: string, params?: Record<string, unknown>): string | null {
  const text = translate.instant(key, params);
  return typeof text === 'string' && text !== key ? text : null;
}

/** Texto para el código del error, o null si no hay que tocar el `message`. */
function codeText(translate: TranslateService, body: any): string | null {
  const type = body?.error_type ?? body?.errorType;
  if (typeof type === 'string' && type && !KEEP_SERVER_MESSAGE.has(type)) {
    // Algunos códigos traen el dato junto al error (limit, used, max_distance_km…)
    return lookup(translate, `API_ERRORS.TYPE.${type}`, body);
  }
  const code = body?.code;
  if (typeof code === 'string' && code) {
    return lookup(translate, `API_ERRORS.AUTH.${code}`);
  }
  return null;
}

/** Nombre del campo como lo escribe Laravel (`start_date` → «start date»), o su traducción. */
function fieldName(translate: TranslateService, field: string): string {
  return lookup(translate, `API_ERRORS.FIELD.${field}`) ?? field.replace(/_/g, ' ');
}

/** Mensajes de un campo a partir de sus reglas, o null si alguna no se conoce. */
function fieldMessages(
  translate: TranslateService,
  field: string,
  rules: Record<string, unknown[]>,
): string[] | null {
  const attribute = fieldName(translate, field);
  const messages: string[] = [];
  for (const [rule, params] of Object.entries(rules ?? {})) {
    const values = Array.isArray(params) ? params.map(String) : [];
    const text = lookup(translate, `API_ERRORS.RULE.${rule}`, {
      field: attribute,
      values: values.join(', '),
      ...Object.fromEntries(values.map((v, i) => [`p${i}`, v])),
    });
    if (!text) return null;
    messages.push(text);
  }
  return messages.length ? messages : null;
}

/**
 * Recompone `errors` y `message` de un 422 con `failed_rules`. Un campo cuyas
 * reglas no se conocen todas, o que tiene más mensajes que reglas (las closures
 * no se informan), se queda con sus mensajes del servidor. `message` solo se
 * recompone si se han traducido todos los campos.
 */
function translateValidation(
  translate: TranslateService,
  body: any,
): { errors: Record<string, string[]>; message: string | null } | null {
  const failed = body?.failed_rules;
  const errors = body?.errors;
  if (!failed || typeof failed !== 'object' || !errors || typeof errors !== 'object') return null;
  if (uiLang(translate.currentLang) === 'es') return null;

  const translated: Record<string, string[]> = { ...errors };
  let changed = false;
  let complete = true;
  for (const field of Object.keys(errors)) {
    const rules = failed[field];
    const original = errors[field];
    const messages = rules ? fieldMessages(translate, field, rules) : null;
    if (messages && Array.isArray(original) && messages.length === original.length) {
      translated[field] = messages;
      changed = true;
    } else {
      complete = false;
    }
  }
  if (!changed) return null;
  if (!complete) return { errors: translated, message: null };

  const all = Object.values(translated).flat();
  const rest = all.length - 1;
  const first = all[0] ?? '';
  const message = rest > 0 && MORE_ERRORS.test(String(body.message ?? ''))
    ? lookup(translate, 'API_ERRORS.AND_MORE', { message: first, count: rest }) ?? first
    : first;
  return { errors: translated, message };
}

/**
 * Añade una clave que no se enumera: se lee con `body.clave`, pero no aparece
 * en `for…in`, `Object.keys/values` ni `JSON.stringify`. Muchas pantallas montan
 * el aviso recorriendo todas las claves del cuerpo del error (`extractText`), y
 * así no pintan el texto original ni los parámetros de `failed_rules`.
 */
function hide(obj: any, key: string, value: unknown): void {
  Object.defineProperty(obj, key, { value, enumerable: false, writable: true, configurable: true });
}

/**
 * Devuelve el cuerpo del error con los textos en el idioma del dashboard, o el
 * mismo objeto si no hay nada que cambiar. El texto original queda en
 * `server_message` / `server_errors`, y `failed_rules` sigue ahí; las tres sin
 * enumerar (ver `hide`).
 */
export function translateApiErrorBody(translate: TranslateService, body: any): any {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body;

  const next: any = { ...body };
  let changed = false;
  if ('failed_rules' in body) {
    hide(next, 'failed_rules', body.failed_rules);
    changed = true;
  }

  const text = codeText(translate, body);
  if (text) {
    hide(next, 'server_message', body.message ?? body.error);
    // Cuatro códigos traen el texto en `error` en vez de `message`
    if (typeof body.error === 'string' && body.message === undefined) next.error = text;
    else next.message = text;
    // `hideSummary` devuelve si ha ocultado algo, no el cuerpo: devolver su
    // resultado dejaba el error sin cuerpo (`false` → `null`) y las pantallas
    // perdían `error_type` y `message`.
    hideSummary(next);
    return next;
  }

  const validation = translateValidation(translate, body);
  if (validation) {
    next.errors = validation.errors;
    hide(next, 'server_errors', body.errors);
    if (validation.message !== null) {
      next.message = validation.message;
      hide(next, 'server_message', body.message);
    }
    changed = true;
  }

  const summarized = hideSummary(next);
  return changed || summarized ? next : body;
}

/**
 * En un 422 de validación, `message` es el primer error de `errors` más
 * «(y N más)». Las pantallas que pintan todas las claves del cuerpo
 * (`extractText`) lo mostraban junto a la lista y el primer error salía dos
 * veces. Si `errors` trae algún texto, `message` deja de enumerarse: quien lo
 * lee como `err.error.message` lo sigue teniendo.
 */
function hideSummary(next: any): boolean {
  const errors = next.errors;
  if (typeof next.message !== 'string' || !errors || typeof errors !== 'object') return false;
  const hasText = Object.values(errors).some((v) =>
    typeof v === 'string' || (Array.isArray(v) && v.some((m) => typeof m === 'string' && m)),
  );
  if (!hasText) return false;
  hide(next, 'message', next.message);
  return true;
}

/** El mismo HttpErrorResponse con el cuerpo traducido (o el original si no cambia). */
export function translateApiError(translate: TranslateService, error: HttpErrorResponse): HttpErrorResponse {
  const body = translateApiErrorBody(translate, error.error);
  if (body === error.error) return error;
  return new HttpErrorResponse({
    error: body,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  });
}
